import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import { toast } from "react-toastify";
import ReactSelect from "react-select";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { reactSelectStyling } from "../../../Components/Common/Forms/FormikInputHelper";
import { canEdit } from "../../../helpers/permissions";
import { useKitchenStore } from "../../../stores/Assets/kitchen";

const KitchenAppSettings = () => {
  const { kitchenId } = useParams();
  const navigate = useNavigate();
  const { getServiceAppSettings, updateServiceAppSettings } = useKitchenStore(
    (state) => state,
  );
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canEditKitchen, setCanEditKitchen] = useState(false);

  document.title = t("Service app settings");

  useEffect(() => {
    canEdit("kitchen").then(setCanEditKitchen);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getServiceAppSettings(kitchenId);
        if (!active) return;
        const allowed = new Set((data?.menus || []).map((menu) => menu.menu_id));
        setSettings({
          ...data,
          focus_menus: (data?.focus_menus || []).filter((id) => allowed.has(id)),
        });
      } catch (error) {
        if (active) setSettings(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [kitchenId, getServiceAppSettings]);

  const menuOptions = (settings?.menus || []).map((menu) => ({
    value: menu.menu_id,
    label: menu.name || `#${menu.menu_id}`,
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      connection: settings?.connection || "POOLING",
      focus_menus: settings?.focus_menus || [],
    },
    validationSchema: Yup.object({
      connection: Yup.string().oneOf(["POOLING", "SOCKET"]).required(t("Required")),
      focus_menus: Yup.array().of(Yup.number()),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const updated = await updateServiceAppSettings(kitchenId, {
          connection: values.connection,
          focus_menus: values.focus_menus,
        });
        const allowed = new Set((updated?.menus || []).map((menu) => menu.menu_id));
        setSettings({
          ...updated,
          focus_menus: (updated?.focus_menus || []).filter((id) => allowed.has(id)),
        });
        toast.success(t("Service app settings saved"));
      } catch (error) {
        // Error toast is handled by the axios interceptor
      } finally {
        setSaving(false);
      }
    },
  });

  const selectedMenus = menuOptions.filter((option) =>
    formik.values.focus_menus.includes(option.value),
  );

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">
              {settings?.name
                ? `${t("Service app settings")} - ${settings.name}`
                : t("Service app settings")}
            </h4>
            <Button color="secondary" size="sm" onClick={() => navigate("/kitchens")}>
              <i className="ri-arrow-left-line"></i> {t("Back")}
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner color="primary" />
        </div>
      ) : !settings ? (
        <div className="text-muted">{t("Could not load service app settings")}</div>
      ) : (
        <Row>
          <Col lg={8}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">{t("How this station receives orders")}</h5>
              </CardHeader>
              <CardBody>
                <form onSubmit={formik.handleSubmit}>
                  <div className="mb-3">
                    <Label htmlFor="connection" className="form-label">
                      {t("Connection")}
                    </Label>
                    <Input
                      id="connection"
                      name="connection"
                      type="select"
                      value={formik.values.connection}
                      onChange={formik.handleChange}
                      disabled={!canEditKitchen || saving}
                    >
                      <option value="SOCKET">{t("Socket")}</option>
                      <option value="POOLING">{t("Pooling")}</option>
                    </Input>
                  </div>

                  <div className="mb-4">
                    <Label className="form-label">{t("Focus menus")}</Label>
                    <ReactSelect
                      isMulti
                      options={menuOptions}
                      value={selectedMenus}
                      isDisabled={!canEditKitchen || saving}
                      placeholder={t("Select menus")}
                      onChange={(selected) =>
                        formik.setFieldValue(
                          "focus_menus",
                          (selected || []).map((option) => option.value),
                        )
                      }
                      {...reactSelectStyling}
                    />
                    <div className="text-muted mt-1">
                      {t("Orders from these menus are highlighted on the station tablets.")}
                    </div>
                  </div>

                  {canEditKitchen && (
                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t("Saving")}
                        </>
                      ) : (
                        t("Save")
                      )}
                    </Button>
                  )}
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default KitchenAppSettings;
